import {memo, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEllipsisVertical} from "@fortawesome/pro-regular-svg-icons/faEllipsisVertical";
import {faSignOut} from "@fortawesome/pro-light-svg-icons/faSignOut";

import Button from "@/components/Global/Button/Button";
import CustomerPicture from "@/components/Global/CustomerPicture/CustomerPicture";
import Icon from "@/components/Global/Icon/Icon";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import {checkIsDemoAccount} from "@/helpers/checkIsDemoAccount";
import {useMediaQuery} from "@/hooks/useMediaQuery/useMediaQuery";
import {useWhiteLabelTheme} from "@/hooks/useWhiteLabelTheme/useWhiteLabelTheme";
import {logout} from "@/features/auth";

import styles from "./UserProfileHeader.module.scss";
import {selectUserDetails} from "@/features/user/userSelectors";

const THEME_TOGGLE = {
  forth: <Icon icon="forth-icon-gradient" />,
  edge: <Icon icon="favicon-edge" />,
  bt: "BT",
  custom: "WL",
};

export default memo(function UserProfileHeader() {
  const customer = useSelector(selectUserDetails);
  const isDemoAccount = useMemo(() => checkIsDemoAccount(customer?.ID), [customer?.ID]);
  const {themeConfig, theme, toggleTheme} = useWhiteLabelTheme();
  const whiteLabelLogo = themeConfig?.assets?.mobileHeaderLogoPath || null;
  const {isMobile, isDesktop, isTablet} = useMediaQuery();

  const dispatch = useDispatch();

  return (
    <div className={"row gap-2 gap-md-0 mx-0 align-items-center py-3"}>
      <RenderIf isTrue={!whiteLabelLogo || (whiteLabelLogo && isMobile)}>
        <div className="col-auto">
          <CustomerPicture className={styles.customerPicture} whiteLabelLogo={whiteLabelLogo} />
        </div>
      </RenderIf>
      <div className="col">
        <h2 className="mb-0">Hi, {customer?.customerFirstName}</h2>
      </div>
      <div className="col-auto gx-3 d-flex">
        {/* <Button variant="transparent">
          <FontAwesomeIcon icon={faBell} size="lg" />
        </Button> */}
        <RenderIf isTrue={window.config.isDev}>
          <div className={`${styles.containerThemeToggle} me-2`}>
            <span className={`d-flex lh-base fw-semibold cursor-pointer ${styles.themeToggle}`}>
              {THEME_TOGGLE[theme]}
            </span>
            {["forth", "edge", "bt", "custom"].map(t => {
              return (
                <Button
                  key={t}
                  variant="transparent"
                  data-activated={theme === t}
                  onClick={() => {
                    toggleTheme(t);
                    window.location.reload();
                  }}
                  className="p-0 px-2">
                  <span className={`${styles.themeToggle}`}>{THEME_TOGGLE[t]}</span>
                </Button>
              );
            })}
          </div>
        </RenderIf>

        {/* <RenderIf
          isTrue={
            ["localhost", "appdev.forthwithlife.co.uk", "appdev.forthedge.co.uk"].includes(
              window.location.hostname,
            ) || config.websiteId == 2
          }>
          <Button variant="transparent" onClick={toggleVariant}>
            <span className={`${styles.themeToggle}`}>
              {variant == "light" ? (
                <FontAwesomeIcon icon={faMoon} size="lg" className="fa-fw" />
              ) : (
                <FontAwesomeIcon icon={faSunBright} size="lg" className="fa-fw" />
              )}
            </span>
          </Button>
        </RenderIf> */}

        <RenderIf isTrue={!isDemoAccount}>
          <Button
            variant="transparent"
            href="/my-account"
            className="d-inline-block d-md-none"
            aria-label="go to my account">
            <FontAwesomeIcon className={`${styles.iconSize}`} icon={faEllipsisVertical} size="lg" />
          </Button>
        </RenderIf>

        <RenderIf isTrue={isDemoAccount && isMobile}>
          <Button
            variant="transparent"
            className="d-flex flex-column justify-content-center align-items-center"
            onClick={() => dispatch(logout())}>
            <FontAwesomeIcon icon={faSignOut} size="xl" className="mt-auto mb-1" />
            <small className={styles.logoutCopy}>Logout</small>
          </Button>
        </RenderIf>
      </div>
    </div>
  );
});
